import { ReactNode, useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

export interface SelectOption {
    value: string;
    label: ReactNode;
}

/** Normalisation accent-insensible pour la recherche interne. */
const norm = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Texte recherchable d'une option (label string, sinon la valeur). */
const optionText = (opt: SelectOption): string =>
    typeof opt.label === 'string' ? opt.label : String(opt.value);

/**
 * Select custom Tailwind — remplace MUI Select.
 * Recherche intégrée : taper dans le champ filtre les options (désactivable via searchable={false}).
 *
 * Exemple :
 *   <Select label="Entité" value={val} onChange={setVal}
 *           options={[{ value: 'F1', label: 'F1 - Siège' }]} />
 */
const Select = (props: {
    label?: ReactNode;
    value?: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    error?: boolean;
    className?: string;
    /** Affiche le champ de recherche dans le dropdown (défaut true). */
    searchable?: boolean;
    /** Placeholder du champ de recherche. */
    searchPlaceholder?: string;
}) => {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const ref = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const options = Array.isArray(props.options) ? props.options : [];

    const searchable = props.searchable !== false;

    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, []);

    // Réinitialise la recherche + focus à l'ouverture
    useEffect(() => {
        if (open) {
            setQuery('');
            if (searchable) setTimeout(() => searchRef.current?.focus(), 0);
        }
    }, [open, searchable]);

    const filtered = query.trim()
        ? options.filter(o => norm(optionText(o)).includes(norm(query.trim())) || norm(String(o.value)).includes(norm(query.trim())))
        : options;

    const selected = options.find(o => o.value === props.value);

    const choose = (v: string) => {
        props.onChange(v);
        setOpen(false);
    };

    return (
        <div className={`w-full ${props.className || ''}`} ref={ref}>
            {props.label && (
                <label className="block text-xs font-semibold text-[var(--eb-text-muted)] mb-1.5">
                    {props.label}
                </label>
            )}
            <div className="relative">
                <button
                    type="button"
                    disabled={props.disabled}
                    onClick={() => setOpen(!open)}
                    className={`w-full flex items-center justify-between px-3 py-2 text-sm rounded-lg border bg-[var(--eb-surface)] text-left
                        focus:outline-none focus:ring-2 focus:ring-[var(--eb-primary)]/40 focus:border-[var(--eb-primary)]
                        transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed
                        ${props.error ? 'border-red-500' : 'border-[var(--eb-border)]'}
                        ${selected ? 'text-[var(--eb-text)]' : 'text-[var(--eb-text-muted)]/60'}`}
                >
                    <span className="truncate">{selected ? selected.label : (props.placeholder || 'Sélectionner...')}</span>
                    <ChevronDown size={16} className={`flex-shrink-0 text-[var(--eb-text-muted)] transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>

                {open && (
                    <div className="absolute z-50 w-full mt-1 py-1 bg-[var(--eb-surface)] border border-[var(--eb-border)] rounded-lg shadow-lg">
                        {searchable && (
                            <div className="px-2 pb-1.5 pt-0.5 border-b border-[var(--eb-border)] mb-1">
                                <div className="relative">
                                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--eb-text-muted)]" />
                                    <input
                                        ref={searchRef}
                                        type="text"
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && filtered.length > 0) {
                                                e.preventDefault();
                                                choose(filtered[0].value);
                                            }
                                            e.stopPropagation();
                                        }}
                                        placeholder={props.searchPlaceholder || 'Rechercher...'}
                                        className="w-full pl-8 pr-2 py-1.5 text-sm rounded-md border border-[var(--eb-border)] bg-[var(--eb-bg)] text-[var(--eb-text)]
                                            placeholder:text-[var(--eb-text-muted)]/60 focus:outline-none focus:ring-2 focus:ring-[var(--eb-primary)]/40"
                                    />
                                </div>
                            </div>
                        )}
                        <div className="max-h-60 overflow-y-auto">
                            {filtered.length === 0 && (
                                <div className="px-3 py-2 text-xs text-[var(--eb-text-muted)]">
                                    {options.length === 0 ? 'Aucune option' : `Aucun résultat pour « ${query} »`}
                                </div>
                            )}
                            {filtered.map(opt => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => choose(opt.value)}
                                    className={`w-full flex items-center justify-between px-3 py-2 text-sm text-left
                                        hover:bg-[var(--eb-primary-soft)] transition-colors
                                        ${opt.value === props.value ? 'text-[var(--eb-primary)] font-semibold' : 'text-[var(--eb-text)]'}`}
                                >
                                    <span className="truncate">{opt.label}</span>
                                    {opt.value === props.value && <Check size={14} />}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Select;
